import React from "react";

import { Divider, Grid, Stack, Typography } from "@mui/material";

interface CardsViewShellProps {
  children: React.ReactNode;
  shownCount: number;
  totalCount: number;
}

/**
 * Shared shell for scrollable card-grid style views.
 * Handles internal scrolling, shadow gutter space, and the shared count footer.
 */
const CardsViewShell: React.FC<CardsViewShellProps> = ({
  children,
  shownCount,
  totalCount,
}) => {
  const scrollPaddingRef = React.useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    const scrollElement = scrollPaddingRef.current;
    if (!scrollElement) {
      return;
    }

    const measure = () => {
      setScrollbarWidth(scrollElement.offsetWidth - scrollElement.clientWidth);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(scrollElement);

    if (scrollElement.firstElementChild) {
      observer.observe(scrollElement.firstElementChild);
    }

    return () => observer.disconnect();
  }, [children]);

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        py: 1.5,
        overflow: "visible",
      }}
    >
      <Stack
        ref={scrollPaddingRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 1,
          mx: -1,
          pb: 2,
          pr: `calc(${scrollbarWidth}px)`,
        }}
      >
        <Grid container spacing={2}>
          {children}
        </Grid>
      </Stack>

      <>
        <Divider sx={{ flexShrink: 0, mb: 1 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ flexShrink: 0 }}
        >
          Showing {shownCount} of {totalCount}
        </Typography>
      </>
    </Stack>
  );
};

export default CardsViewShell;